import type { OutputData } from '@editorjs/editorjs';
import type { ZodError } from 'zod';
import type { BlockInputProps } from '../../../components/BlockInput/BlockInput';

import { useStore } from '@tanstack/react-form';
import { useMemo } from 'react';

import BlockInput from '../../../components/BlockInput/BlockInput';

import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const BlockField = ({
  onFocus,
  onBlur,
  disabled,
  ...props
}: BlockInputProps) => {
  const field = useFieldContext<OutputData>();
  const errors = useFieldErrors(field.name);
  const yjsField = useYjsField('raw');

  const form = useFormContext();
  const blocksError = useStore(
    form.store,
    ({ errors }) =>
      errors[0]?.[`${field.name}.blocks`] as (ZodError | string)[],
  );

  const finalErrors = useMemo(
    () => [
      ...(errors || []),
      ...(blocksError
        ? [
            blocksError.map((error) =>
              typeof error === 'string' ? error : error.message,
            ),
          ]
        : []),
    ],
    [blocksError, errors],
  );

  const onChange = (value: OutputData) => {
    field.handleChange(value);
    yjsField.update(value);
  };

  const onFieldFocus = (name?: string) => {
    onFocus?.(name);
    yjsField.onFocus();
  };

  const onFieldBlur = (name?: string) => {
    onBlur?.(name);
    field.handleBlur();
    yjsField.onBlur();
  };

  return (
    <BlockInput
      {...props}
      name={field.name}
      value={field.state.value}
      onChange={onChange}
      error={finalErrors}
      onFocus={onFieldFocus}
      onBlur={onFieldBlur}
      disabled={disabled || yjsField.isBeingEdited}
      editors={yjsField.fieldEditors}
    />
  );
};

export default BlockField;
